import { makeStyles } from '@material-ui/core';
import { LevelOfSkillDto } from 'utils/wemble-api.generated';
import { LEVEL_FILLED, LEVEL_FILLED_ZERO } from './SkillLevelTrack';

const useStyles = makeStyles({
  dots: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  dotWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'default',
    padding: '8px 0',
    margin: '-8px 0',
    '&:hover $tooltip': {
      display: 'block',
    },
  },
  // Square cells rather than circles, matching the segmented level track on the
  // person card's competence grid. Kept to the caller's dot size so the
  // footprint is unchanged in the tight contexts this renders in (the shortlist
  // rail's fixed-width competence cells, the homepage annotation rows).
  dot: {
    borderRadius: 2,
    boxSizing: 'border-box',
  },
  // display: none rather than visibility: hidden, so the hidden nowrap tooltip
  // takes no layout: long level names otherwise widen the scroll area past the
  // viewport on mobile (same fix as CompetenceAverageSkillLevel).
  tooltip: {
    display: 'none',
    position: 'absolute',
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(34,34,34,0.8)',
    color: 'white',
    textAlign: 'center',
    borderRadius: 4,
    padding: '4px 8px',
    zIndex: 1,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    fontSize: 11,
    marginBottom: 4,
  },
});

// Same fills as the editable SkillLevelTrack, so the read-only dots and the
// tracks never drift apart.
export const BLUE = LEVEL_FILLED;
export const GREEN = LEVEL_FILLED_ZERO;
export const GREY = '#E5E5E5';

const CELL_GAP = 1;

/** Filled up to userLevel, grey above. userLevel < 0 means the person does not
 *  hold the competence at all.
 *
 *  `dotSize`/`dotGap` keep their names and their callers' level-count scaling,
 *  but now describe a cell: the gap is folded into the cell width and the cells
 *  sit 1px apart, so the track reads as one segmented bar (matching the person
 *  card's competence grid) within a pixel of the old footprint — which matters,
 *  because the staffing shortlist renders this inside a fixed-width column. */
const SkillLevelDots = ({
  levels,
  userLevel,
  dotSize,
  dotGap,
  requiredLevel,
}: {
  levels: LevelOfSkillDto[];
  userLevel: number;
  dotSize: number;
  dotGap: number;
  /** Outlines the level a staffing request asks for */
  requiredLevel?: number | null;
}) => {
  const classes = useStyles();

  return (
    <div className={classes.dots}>
      {levels.map((level, i) => {
        const isRequired = requiredLevel != null && requiredLevel > 0 && level.level === requiredLevel;
        return (
          <div key={level._id || i} className={classes.dotWrapper} style={{ marginLeft: i > 0 ? CELL_GAP : 0 }}>
            <div
              className={classes.dot}
              style={{
                width: Math.max(dotSize, dotSize + dotGap - CELL_GAP),
                height: dotSize,
                backgroundColor:
                  (level.level ?? 0) <= userLevel ? (userLevel === 0 ? GREEN : BLUE) : GREY,
                border: isRequired ? '1px solid #8A99A5' : undefined,
              }}
            />
            <div className={classes.tooltip}>
              {level.name}
              {isRequired ? ' · required' : ''}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SkillLevelDots;
